import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SearchResult } from '../../models/search-result.model';
import { FACET_CONFIGS } from '../../constants/facet-configs';
import { FACET_VALUE_ICONS } from '../../constants/facet-value-icons';
import { FacetConfig, FacetChipConfig } from '../../models/facet-config.model';
import { resolveResultLink } from '../../utils/result-link.util';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';
import { TranslationService } from '../../services/translation.service';

interface MetaBadge {
  key: string;
  label: string;
  displayLabel: string;
  icon: string | null;
  showLabel: boolean;
  cssClass?: string;
}

interface ResultLink {
  href: string;
  target: '_blank' | '_self';
  rel: string | null;
}

interface InstitutionStatChip {
  label: string;
  cssClass: string;
}

const FACET_CONFIG_MAP = new Map<string, FacetConfig>(
  FACET_CONFIGS.map(config => [config.key, config])
);

interface ProcessedValue {
  label: string;
  lookupKey?: string;
}

interface BadgeDescriptor {
  key: string;
  extract: (item: SearchResult) => ProcessedValue[];
}

const BADGE_DESCRIPTORS: BadgeDescriptor[] = [
  {
    key: 'typeId',
    extract: item => {
      if (!item.typeLabel && !item.typeId) return [];
      return [{
        label: item.typeLabel ?? item.typeId!,
        lookupKey: item.typeId
      }];
    }
  },
  {
    key: 'availabilityTypeId',
    extract: item => {
      if (!item.availabilityTypeLabel) return [];
      return [{
        label: item.availabilityTypeLabel,
        lookupKey: item.availabilityTypeId ?? item.availabilityTypeLabel
      }];
    }
  },
  {
    key: 'targetAudience',
    extract: item => (item.targetAudience ?? []).map(v => ({ label: v, lookupKey: v }))
  },
  {
    key: 'areas',
    extract: item => (item.areas ?? []).map(v => ({ label: v, lookupKey: v }))
  }
];

@Component({
  selector: 'app-result-item',
  standalone: true,
  imports: [CommonModule, CapitalizePipe],
  templateUrl: './result-item.component.html',
  styles: [`
    .hit-breadcrumb {
      font-size: 0.85rem;
      color: #666;
      margin-bottom: 4px;
      font-weight: 500;
    }

    .institution-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.875rem;
      margin-top: 0.25rem;
    }

    .hit-meta-container--footer {
      margin-top: 0;
      flex: 0 0 auto;
    }

    .institution-stats {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      flex-wrap: nowrap;
      flex: 0 0 auto;
      margin-top: 0;
    }

    .institution-stat-chip {
      margin-top: 0;
      white-space: nowrap;
    }
  `]
})
export class ResultItemComponent {
  @Input() item!: SearchResult;
  @Input() hideFacets = false;
  @Input() position = 0;
  @Output() resultClick = new EventEmitter<{ id: string; typeId?: string; position: number }>();

  constructor(private readonly translation: TranslationService) {}

  onLinkClick(): void {
    if (!this.item) {
      return;
    }
    this.resultClick.emit({ id: this.item.id, typeId: this.item.typeId, position: this.position });
  }

  get showBreadcrumb(): boolean {
    return !this.hideFacets && Boolean(this.breadcrumb);
  }

  get breadcrumb(): string {
    if (!this.item) {
      return '';
    }

    const typeId = this.item.typeId;
    const arrow = ' > ';

    // 3: Pagina informativa
    if (typeId === '3') {
      return [this.item.domainLabel, this.item.categoryLabel].filter(Boolean).join(arrow);
    }

    // 1: Procedura
    if (typeId === '1') {
      return [this.item.countyLabel, this.item.cityLabel, this.item.institutionName].filter(Boolean).join(arrow);
    }

    // 2: Institutie
    if (typeId === '2') {
       return [this.item.countyLabel, this.item.cityLabel].filter(Boolean).join(arrow);
    }

    return '';
  }

  get description(): string {
    if (!this.item) {
      return '';
    }
    return this.item.description ?? '';
  }

  get locationLabel(): string {
    if (!this.item) {
      return '';
    }
    return [this.item.countyLabel, this.item.cityLabel].filter(Boolean).join(', ');
  }

  get metaBadges(): MetaBadge[] {
    if (!this.item) {
      return [];
    }

    const badges: MetaBadge[] = [];

    BADGE_DESCRIPTORS.forEach(descriptor => {
      const rawValues = descriptor.extract(this.item)
        .filter(v => Boolean(v.label) && v.label.trim().length > 0);

      if (!rawValues.length) {
        return;
      }

      // Deduplicate by label
      const distinctValues = new Map<string, ProcessedValue>();
      rawValues.forEach(v => distinctValues.set(v.label, v));

      distinctValues.forEach(valueObj => {
        const badge = this.createBadge(descriptor.key, valueObj.label, valueObj.lookupKey);
        if (badge) {
          badges.push(badge);
        }
      });
    });

    return badges;
  }

  get institutionStatChips(): InstitutionStatChip[] {
    if (!this.item || this.item.typeId !== '2') {
      return [];
    }

    const proceduresCount = Math.max(0, Math.trunc(this.item.proceduresCount ?? 0));
    if (proceduresCount <= 0) {
      return [];
    }

    const onlineProceduresCount = Math.min(
      proceduresCount,
      Math.max(0, Math.trunc(this.item.onlineProceduresCount ?? 0))
    );
    const onlinePercent = Math.round((onlineProceduresCount / proceduresCount) * 100);

    return [
      {
        label: this.translation.translate('institutionProceduresCount', {
          count: proceduresCount
        }),
        cssClass: 'chip-typeId'
      },
      {
        label: this.translation.translate('institutionOnlinePercent', {
          count: onlinePercent
        }),
        cssClass: 'chip-availabilityTypeId'
      }
    ];
  }

  private createBadge(key: string, value: string, lookupKey?: string): MetaBadge | null {
    if (!value) {
      return null;
    }

    const chipConfig = this.getChipConfig(key);
    
    // Check for value-specific icon
    let icon = chipConfig?.icon ?? null;
    let showLabel = chipConfig?.showLabel !== false;

    const valueIcons = FACET_VALUE_ICONS[key];
    if (valueIcons) {
      // Try looking up by lookupKey first, then by label
      const specificIcon = (lookupKey && valueIcons[lookupKey]) || valueIcons[value];
      if (specificIcon) {
        icon = specificIcon;
        showLabel = false; // Hide label if we have a specific icon
      }
    }

    return {
      key,
      label: value,
      displayLabel: this.getDisplayLabel(value),
      icon,
      showLabel,
      cssClass: chipConfig?.cssClass
    };
  }

  private getChipConfig(key: string): FacetChipConfig | undefined {
    return FACET_CONFIG_MAP.get(key)?.chip;
  }

  private getDisplayLabel(label: string): string {
    if (label.length <= 30) {
      return label;
    }
    return `${label.slice(0, 30)}…`;
  }

  get resultLink(): ResultLink | null {
    if (!this.item) {
      return null;
    }

    const resolved = resolveResultLink(this.item);
    if (!resolved) {
      return null;
    }

    return {
      href: resolved.href,
      target: resolved.target,
      rel: resolved.rel
    };
  }
}
